import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { PackagingLotStockOutForm } from './new'
import type { CreateOutNoteRequest } from '@/lib/api/packaging-lot-api'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
    usePackagingOutNote,
    useUpdatePackagingOutNote,
} from '@/hooks/use-packaging-lot-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/stock-out-notes/$id_/edit',
)({
    component: Page,
})

function Page() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data, isLoading } = usePackagingOutNote(id)
    const note = data?.data
    const update = useUpdatePackagingOutNote()

    const seedOrderOptions = useMemo(() => {
        if (!note?.order_id) return []
        return [
            {
                value: note.order_id,
                label: note.order_code ?? note.order_id,
            },
        ]
    }, [note])

    if (isLoading) return <div className="p-6">{m.sl_detail_loading()}</div>
    if (!note) return <div className="p-6">{m.sl_detail_note_not_found()}</div>

    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && !note.stocktake_session_id

    if (!editable) {
        return (
            <div className="space-y-4 p-6">
                <Button asChild variant="ghost" className="-ml-2">
                    <Link
                        to="/packaging-warehouse/stock-out-notes/$id"
                        params={{ id }}
                    >
                        <ArrowLeft className="size-4 mr-1" />
                        {m.sl_lot_back()}
                    </Link>
                </Button>
                <p className="text-muted-foreground">
                    {m.pl_pnk_edit_not_allowed()}
                </p>
            </div>
        )
    }

    const initialValues = {
        reason: note.reason,
        order_id: note.order_id ?? '',
        notes: note.notes ?? '',
        items: (note.items ?? []).map((it) => ({
            kind: 'existing' as const,
            packaging_lot_id: it.packaging_lot_id,
            quantity: it.quantity,
        })),
    }

    const backToDetail = () =>
        navigate({
            to: '/packaging-warehouse/stock-out-notes/$id',
            params: { id },
        })

    const handleSubmit = (value: CreateOutNoteRequest) => {
        update.mutate(
            {
                id,
                req: {
                    reason: value.reason,
                    notes: value.notes,
                    items: value.items,
                },
            },
            {
                onSuccess: () => void backToDetail(),
                onError: (err) =>
                    toast.error(
                        err instanceof Error
                            ? err.message
                            : m.sl_new_error_create(),
                    ),
            },
        )
    }

    return (
        <PackagingLotStockOutForm
            mode="edit"
            title={m.pkl_pxk_edit_title()}
            submitLabel={m.pl_pnk_edit_submit()}
            initialValues={initialValues}
            seedOrderOptions={seedOrderOptions}
            isPending={update.isPending}
            onCancel={() => void backToDetail()}
            onSubmit={handleSubmit}
        />
    )
}
